// 「今日の宇宙のひとこと」の固定文面
// 健康・運勢の断定は避け、「〜に向いた日です」程度の穏やかな表現に留める。
// 後から九星の日の運勢と組み合わせられるよう、文面は (key, ctx) から引く関数にしておく。
import type { MoonPhaseKey } from './moon';

export type MessageContext = {
  dayStar?: number;   // 日盤の中宮星（将来の組み合わせ用）
  honmeiStar?: number | null;
};

const MOON_MESSAGES: Record<MoonPhaseKey, string> = {
  new: '新しいことの種をまくのに向いた日です。',
  crescent: '小さな一歩を踏み出すのに向いた日です。',
  waxing: '少しずつ積み重ねるのに向いた日です。',
  firstQuarter: '物事を育てはじめるのに向いた日です。',
  gibbous: '形を整え、仕上げに向かうのに向いた日です。',
  full: 'これまでの実りを味わい、感謝するのに向いた日です。',
  izayoi: 'ひと息ついて、ゆっくり振り返るのに向いた日です。',
  waning: '分かち合い、人に手渡すのに向いた日です。',
  lastQuarter: '手放しや片づけに向いた日です。',
  ariake: '静かに休み、次の新月に備えるのに向いた日です。',
};

// 二十四節気（SEKKI_NAMES と同じ順：春分から）
const SEKKI_MESSAGES: string[] = [
  '昼と夜が同じ長さになる日。心と暮らしのバランスを整えるのに向いています。', // 春分
  '万物が清らかに明るくなる頃。外に出て、空気の変化を感じてみましょう。', // 清明
  '穀物を育てる雨が降る頃。始めたことに、そっと水をやるように続けましょう。', // 穀雨
  '暦の上で夏が始まる日。新しい季節の勢いに乗るのに向いています。', // 立夏
  '草木が満ちはじめる頃。小さな手応えを喜ぶのに向いています。', // 小満
  '穂の出る穀物の種をまく頃。根気よく取り組むのに向いています。', // 芒種
  '一年でいちばん昼が長い日。太陽の力を浴びて、のびやかに過ごしましょう。', // 夏至
  '暑さが本格的になる手前。無理をせず、ペースを整えるのに向いています。', // 小暑
  '一年でいちばん暑い頃。涼しい時間を上手に使いましょう。', // 大暑
  '暦の上で秋が始まる日。夏の疲れをいたわり、次の実りに目を向けましょう。', // 立秋
  '暑さがおさまっていく頃。ひと区切りつけるのに向いています。', // 処暑
  '草に白い露が宿る頃。朝の澄んだ空気を味わってみましょう。', // 白露
  '昼と夜が同じ長さになる日。実りに感謝し、分かち合うのに向いています。', // 秋分
  '露が冷たく感じられる頃。温かいものでひと息つきましょう。', // 寒露
  '霜が降りはじめる頃。冬支度を少しずつ始めるのに向いています。', // 霜降
  '暦の上で冬が始まる日。内側を見つめ、力を蓄えるのに向いています。', // 立冬
  'わずかに雪が舞いはじめる頃。早めに休み、ゆっくり整えましょう。', // 小雪
  '本格的に雪が降る頃。身近な人との時間を大切にするのに向いています。', // 大雪
  '一年でいちばん夜が長い日。ここから光が戻ってきます。', // 冬至
  '寒さが厳しくなる入り口。一年の計を静かに描くのに向いています。', // 小寒
  '一年でいちばん寒い頃。春を待つ気持ちで、足元を固めましょう。', // 大寒
  '暦の上で春が始まる日。九星気学では新しい一年の始まりです。', // 立春
  '雪が雨に変わる頃。固まっていたものがほどけていくのに向いています。', // 雨水
  '土の中の虫が目を覚ます頃。動き出すきっかけをつかむのに向いています。', // 啓蟄
];

export function sekkiMessage(index: number): string {
  return SEKKI_MESSAGES[index] || '';
}

// 宇宙天気（健康・運勢には結びつけない）
const SPACE_WX_MESSAGES: Record<'quiet' | 'active' | 'storm', string> = {
  quiet: '今日の太陽はおだやか。宇宙も静かな一日です。',
  active: '今日は太陽が元気な日です。',
  storm: '地球のまわりで磁気嵐が起きています。北の国々ではオーロラが舞う夜です。',
};
export function spaceWeatherMessage(level: 'quiet' | 'active' | 'storm'): string {
  return SPACE_WX_MESSAGES[level];
}

// 地球のリズム
const SEASON_MESSAGES: Record<'春' | '夏' | '秋' | '冬', string> = {
  春: '芽吹きの力を感じながら、新しいことを始めるのに向いた季節です。',
  夏: '外へ広がる力が強まり、のびのびと動くのに向いた季節です。',
  秋: '実りを集め、分かち合うのに向いた季節です。',
  冬: '内側に力を蓄え、次の春に備える季節です。',
};
export function seasonMessage(season: '春' | '夏' | '秋' | '冬'): string {
  return SEASON_MESSAGES[season];
}
export function doyoMessage(season: '春' | '夏' | '秋' | '冬'): string {
  return `いまは${season}の土用。季節の変わり目で、暮らしのペースを整えるのに向いた時期です。`;
}
const TIDE_MESSAGES: Record<string, string> = {
  大潮: '潮の満ち引きが大きい日です。',
  中潮: '潮の動きがほどよい日です。',
  小潮: '潮の満ち引きが小さく、海がおだやかな日です。',
  長潮: '潮の動きがゆるやかな日です。',
  若潮: '潮がふたたび大きくなりはじめる日です。',
};
export function tideMessage(tide: string): string {
  return TIDE_MESSAGES[tide] || '';
}

// 夜空へのお誘い
export const NIGHT_SKY_INVITE = '今夜ちょっと外に出て、空を見上げてみましょう。';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function moonMessage(key: MoonPhaseKey, _ctx: MessageContext = {}): string {
  return MOON_MESSAGES[key];
}
